import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import axios from 'axios';
import { ROOT_URL } from '../../api/ApiConfig';

export const getKbReviewArticles = createAsyncThunk(
    'kbReview/getKbReviewArticles',
    async ({ business_id, group_id, page = 1, limit = 10, status = 'all', search = '' }, { getState, rejectWithValue }) => {
        try {
            const { loginToken } = getState();
            const authToken = loginToken?.response?.token;

            if (!business_id) throw new Error("Business ID is required");
            if (!group_id) throw new Error("Group ID is required");

            const params = new URLSearchParams();
            params.append('business_id', business_id);
            params.append('group_id', group_id);
            params.append('page', page);
            params.append('limit', limit);

            let queryStatus = status;
            if (status === 'all') {
                queryStatus = 'both';
            }
            params.append('status', queryStatus);

            if (search) {
                params.append('search', search);
            }

            const url = `${ROOT_URL}/kb/articles/draftpending?${params.toString()}`;

            const response = await axios.get(
                url,
                {
                    headers: {
                        'Authorization': `Bearer ${authToken}`,
                        'Content-Type': 'application/json'
                    }
                }
            );

            return response.data;
        } catch (error) {
            return rejectWithValue(error.response?.data || error.message);
        }
    }
);

/** Group-scoped tab counts via draftpending pagination (does not touch the list). */
export const getKbReviewTabCounts = createAsyncThunk(
    'kbReview/getKbReviewTabCounts',
    async ({ business_id, group_id, search = '' }, { getState, rejectWithValue }) => {
        try {
            const { loginToken } = getState();
            const authToken = loginToken?.response?.token;

            if (!business_id) throw new Error("Business ID is required");
            if (!group_id) throw new Error("Group ID is required");

            const fetchTotal = async (status) => {
                const params = new URLSearchParams({
                    business_id,
                    group_id,
                    page: '1',
                    limit: '1',
                    status,
                });
                if (search) params.append('search', search);
                const response = await axios.get(
                    `${ROOT_URL}/kb/articles/draftpending?${params.toString()}`,
                    {
                        headers: {
                            'Authorization': `Bearer ${authToken}`,
                            'Content-Type': 'application/json',
                        },
                    }
                );
                return response.data?.data?.pagination?.total || 0;
            };

            const [all, pendingReview, draft] = await Promise.all([
                fetchTotal('both'),
                fetchTotal('pendingReview'),
                fetchTotal('draft'),
            ]);

            return { all, pendingReview, draft, group_id };
        } catch (error) {
            return rejectWithValue(error.response?.data || error.message);
        }
    }
);

const emptyTabCounts = {
    all: 0,
    pendingReview: 0,
    draft: 0,
};

const initialState = {
    loading: true,
    error: null,
    success: false,
    articles: [],
    totalCount: 0,
    totalPages: 0,
    lastFetchedGroupId: null,
    activeRequestId: null,
    tabCounts: { ...emptyTabCounts },
    tabCountsGroupId: null,
};

const getKbReviewArticlesSlice = createSlice({
    name: 'getKbReviewArticles',
    initialState,
    reducers: {
        resetKbReviewState: (state) => {
            state.loading = false;
            state.error = null;
        }
    },
    extraReducers: (builder) => {
        builder
            .addCase(getKbReviewArticles.pending, (state, action) => {
                state.activeRequestId = action.meta.requestId;
                const groupId = action.meta?.arg?.group_id ?? null;
                const sameGroup =
                    state.lastFetchedGroupId != null &&
                    String(state.lastFetchedGroupId) === String(groupId);
                const hasData = !!state.articles?.length || !!state.totalCount;
                state.loading = !sameGroup || !hasData;
                state.error = null;
            })
            .addCase(getKbReviewArticles.fulfilled, (state, action) => {
                if (state.activeRequestId && action.meta.requestId !== state.activeRequestId) {
                    return;
                }
                state.loading = false;
                state.success = true;
                state.lastFetchedGroupId = action.meta?.arg?.group_id ?? null;
                state.articles = action.payload?.data?.articles || [];
                state.totalCount = action.payload?.data?.pagination?.total || 0;
                state.totalPages = action.payload?.data?.pagination?.totalPages || 0;
            })
            .addCase(getKbReviewArticles.rejected, (state, action) => {
                if (state.activeRequestId && action.meta.requestId !== state.activeRequestId) {
                    return;
                }
                state.loading = false;
                state.error = action.payload;
            })
            .addCase(getKbReviewTabCounts.pending, (state, action) => {
                const groupId = action.meta?.arg?.group_id ?? null;
                const sameGroup =
                    state.tabCountsGroupId != null &&
                    String(state.tabCountsGroupId) === String(groupId);
                if (!sameGroup) {
                    state.tabCounts = { ...emptyTabCounts };
                    state.tabCountsGroupId = groupId;
                }
            })
            .addCase(getKbReviewTabCounts.fulfilled, (state, action) => {
                state.tabCounts = {
                    all: action.payload?.all || 0,
                    pendingReview: action.payload?.pendingReview || 0,
                    draft: action.payload?.draft || 0,
                };
                state.tabCountsGroupId = action.payload?.group_id ?? null;
            })
            .addCase(getKbReviewTabCounts.rejected, (state, action) => {
                const groupId = action.meta?.arg?.group_id ?? null;
                state.tabCounts = { ...emptyTabCounts };
                state.tabCountsGroupId = groupId;
            });
    }
});

export const { resetKbReviewState } = getKbReviewArticlesSlice.actions;
export default getKbReviewArticlesSlice.reducer;
